import { useEffect, useMemo, useState } from 'react'

import {
	FilterBar,
	getSegmentStatusLabel,
	getTodayDateValue,
	normalizeDateWindow,
	normalizeStatusFilterValue,
	resolveDateWindow,
} from './components/FilterBar'
import { ListView } from './components/ListView'
import { ShipmentDetailPanel } from './components/ShipmentDetailPanel'
import { TruckMap } from './components/TruckMap'
import { sendToParent, usePostMessage } from './hooks/usePostMessage'
import { parseToken } from './services/auth'
import { fetchSegments } from './services/segments'

import type { FilterState } from './components/FilterBar'
import type { SegmentFilters } from './services/segments'
import type { MapToken, ParentMessage, TripSegment } from './types'

const DEFAULT_API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787'
const STAGING_API_URL = 'https://trackplg-staging.thepattonlogistics.group'
const isStagingHost =
	typeof window !== 'undefined' && window.location.hostname.startsWith('staging.')
const API_URL = isStagingHost ? STAGING_API_URL : DEFAULT_API_URL
const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN

// View modes
type ViewMode = 'map' | 'list' | 'split'
type TabMode = 'all' | 'in-progress' | 'saved' | 'recent'
type DebugConfig = { enabled: boolean; traceId: string }

// Local storage keys
const STORAGE_KEYS = {
	SAVED_TRIPS: 'shipments-tracker-saved-trips',
	RECENT_TRIPS: 'shipments-tracker-recent-trips',
	FILTER_STATE: 'shipments-tracker-filter-state',
	DEBUG: 'shipments-tracker-debug',
}

// Maximum number of recent items to keep
const MAX_RECENT_ITEMS = 10

function createDebugTraceId(): string {
	if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
		return crypto.randomUUID().replace(/-/g, '').slice(0, 12)
	}

	return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`.slice(0, 12)
}

function isDebugValueEnabled(value: string | null | undefined): boolean {
	if (!value) return false

	return ['1', 'all', 'shipments', 'true'].includes(value.trim().toLowerCase())
}

function readStoredDebugConfig(): DebugConfig {
	try {
		const raw = localStorage.getItem(STORAGE_KEYS.DEBUG)
		if (!raw) {
			return { enabled: false, traceId: '' }
		}

		if (raw === 'true') {
			return { enabled: true, traceId: createDebugTraceId() }
		}

		const parsed = JSON.parse(raw) as { enabled?: boolean; traceId?: string }
		if (!parsed.enabled) {
			return { enabled: false, traceId: '' }
		}

		return {
			enabled: true,
			traceId: parsed.traceId?.trim() || createDebugTraceId(),
		}
	} catch {
		return { enabled: false, traceId: '' }
	}
}

function persistDebugConfig(debugConfig: DebugConfig) {
	try {
		if (!debugConfig.enabled) {
			localStorage.removeItem(STORAGE_KEYS.DEBUG)
			return
		}

		localStorage.setItem(
			STORAGE_KEYS.DEBUG,
			JSON.stringify({
				enabled: true,
				traceId: debugConfig.traceId || createDebugTraceId(),
			})
		)
	} catch {
		// Ignore localStorage errors
	}
}

function summarizeIds(ids: string[], limit = 5) {
	return {
		count: ids.length,
		sample: ids.slice(0, limit),
	}
}

function summarizeToken(token: MapToken) {
	return {
		roles: token.roles || [],
		customer_ids: summarizeIds(token.customer_ids),
		location_ids: summarizeIds(token.location_ids),
		provider: token.provider,
		expires_at: new Date(token.exp * 1000).toISOString(),
	}
}

function summarizeSegments(segments: TripSegment[]) {
	return {
		total: segments.length,
		customers: summarizeIds(
			[...new Set(segments.map((segment) => segment.customer_id).filter(Boolean))],
			5
		),
		locations: summarizeIds(
			[
				...new Set(
					segments
						.map((segment) => segment.location_id)
						.filter((locationId): locationId is string => Boolean(locationId))
				),
			],
			5
		),
		pro_numbers: segments.slice(0, 5).map((segment) => segment.pro_num),
	}
}

function summarizeParentMessage(message: ParentMessage) {
	switch (message.type) {
		case 'setTrips':
			return {
				type: message.type,
				trip_ids: summarizeIds(message.trip_ids),
			}
		case 'setDateRange':
			return {
				type: message.type,
				startDate: message.startDate,
				endDate: message.endDate,
			}
		case 'setDebug':
			return {
				type: message.type,
				enabled: message.enabled,
				traceId: message.traceId,
			}
		case 'fitAll':
			return { type: message.type }
		case 'setStyle':
			return {
				type: message.type,
				provider: message.provider,
				hasStyleURL: Boolean(message.styleURL),
			}
		case 'highlight':
			return {
				type: message.type,
				trip_id: message.trip_id,
			}
		case 'updateSegment':
			return {
				type: message.type,
				trip_id: message.segment.trip_id,
				pro_num: message.segment.pro_num,
			}
	}
}

function logBrowserDebug(
	debugConfig: DebugConfig,
	message: string,
	details?: unknown,
	level: 'error' | 'info' | 'log' | 'warn' = 'log'
) {
	if (!debugConfig.enabled) return

	const prefix = `[shipments-tracker-embed][${debugConfig.traceId || 'no-trace'}]`
	if (details === undefined) {
		console[level](`${prefix} ${message}`)
		return
	}

	console[level](`${prefix} ${message}`, details)
}

function buildApiDateRange(
	filters: Pick<FilterState, 'anchorDate' | 'customEndDate' | 'customStartDate' | 'dateRange'>
) {
	const dateWindow = resolveDateWindow(filters)
	return dateWindow ? `${dateWindow.startDate},${dateWindow.endDate}` : ''
}

function parseApiDateRange(value: string | null) {
	if (!value) return null

	const [startDate, endDate] = value.split(',')
	if (!startDate || !endDate) return null

	return normalizeDateWindow(startDate, endDate)
}

export default function App() {
	const [token, setToken] = useState<MapToken | null>(null)
	const [allSegments, setAllSegments] = useState<TripSegment[]>([])
	const [rawToken, setRawToken] = useState<string>('')
	const [useCustomIcon, setUseCustomIcon] = useState(false)
	const [urlFilters, setUrlFilters] = useState<SegmentFilters>({})
	const [selectedTripId, setSelectedTripId] = useState<string | null>(null)
	const [fitAllKey, setFitAllKey] = useState(0)
	const [savedTripIds, setSavedTripIds] = useState<Set<string>>(new Set())
	const [recentTripIds, setRecentTripIds] = useState<string[]>([])
	const [isInitialized, setIsInitialized] = useState(false)
	const [isLoadingSegments, setIsLoadingSegments] = useState(false)
	const [showServerSearchPrompt, setShowServerSearchPrompt] = useState(false)
	const [debugConfig, setDebugConfig] = useState<DebugConfig>({ enabled: false, traceId: '' })
	const [mapModeBannerDismissed, setMapModeBannerDismissed] = useState(() => {
		try {
			return localStorage.getItem('shipments-tracker-map-banner-dismissed') === 'true'
		} catch {
			return false
		}
	})

	// View mode from URL param (default: split)
	const [viewMode, setViewMode] = useState<ViewMode>('split')

	// Tab mode (default: in-progress to match screenshot)
	const [tabMode, setTabMode] = useState<TabMode>('in-progress')

	// Client-side filters
	const [filters, setFilters] = useState<FilterState>({
		dateRange: '7days',
		anchorDate: getTodayDateValue(),
		keyword: '',
		status: '',
		customerId: '',
		shipperId: '',
		consigneeId: '',
		sortBy: 'pro_num',
		sortDir: 'asc',
		resultsPerPage: 25,
		page: 1,
		mapMode: 'current-page', // Default to current page for better performance
	})

	const applyDebugConfig = (enabled: boolean, traceId?: string) => {
		if (!enabled) {
			logBrowserDebug(debugConfig, 'Debug logging disabled')
			setDebugConfig({ enabled: false, traceId: '' })
			persistDebugConfig({ enabled: false, traceId: '' })
			return
		}

		const nextDebugConfig: DebugConfig = {
			enabled: true,
			traceId: traceId?.trim() || debugConfig.traceId || createDebugTraceId(),
		}

		setDebugConfig(nextDebugConfig)
		persistDebugConfig(nextDebugConfig)
		logBrowserDebug(
			nextDebugConfig,
			'Debug logging enabled',
			{
				traceId: nextDebugConfig.traceId,
			},
			'info'
		)
	}

	// Load saved and recent trips from local storage on mount
	useEffect(() => {
		try {
			const savedData = localStorage.getItem(STORAGE_KEYS.SAVED_TRIPS)
			if (savedData) {
				const savedArray = JSON.parse(savedData) as string[]
				setSavedTripIds(new Set(savedArray))
			}

			const recentData = localStorage.getItem(STORAGE_KEYS.RECENT_TRIPS)
			if (recentData) {
				const recentArray = JSON.parse(recentData) as string[]
				setRecentTripIds(recentArray)
			}
		} catch {
			// Ignore localStorage errors
		}
	}, [])

	// Update API filters when tab mode changes
	useEffect(() => {
		if (!isInitialized) return

		if (tabMode === 'in-progress') {
			// Set is_active filter for in-progress tab
			setUrlFilters((prev) => ({ ...prev, is_active: 'true' }))
		} else {
			// Remove is_active filter for other tabs
			setUrlFilters((prev) => {
				const { is_active: _removed, ...rest } = prev
				return rest
			})
		}
	}, [tabMode, isInitialized])

	// Convert dateRange to API date_range format
	useEffect(() => {
		if (!isInitialized) return

		const dateRangeParam = buildApiDateRange(filters)

		// Update urlFilters with the new date range
		if (dateRangeParam) {
			setUrlFilters((prev) => ({ ...prev, date_range: dateRangeParam }))
		}
	}, [
		filters.anchorDate,
		filters.dateRange,
		filters.customStartDate,
		filters.customEndDate,
		isInitialized,
	])

	// Emit filter changes to parent and save to localStorage
	useEffect(() => {
		// Don't save during initial load - wait for restoration to complete
		if (!isInitialized) return

		const filterState = {
			tabMode,
			dateRange: filters.dateRange,
			anchorDate: filters.anchorDate,
			customStartDate: filters.customStartDate,
			customEndDate: filters.customEndDate,
			keyword: filters.keyword,
			status: filters.status,
			customerId: filters.customerId,
			shipperId: filters.shipperId,
			consigneeId: filters.consigneeId,
			sortBy: filters.sortBy,
			sortDir: filters.sortDir,
			resultsPerPage: filters.resultsPerPage,
			page: filters.page,
			mapMode: filters.mapMode,
			viewMode,
		}

		// Save to localStorage
		try {
			localStorage.setItem(STORAGE_KEYS.FILTER_STATE, JSON.stringify(filterState))
		} catch {
			// Ignore localStorage errors
		}

		// Emit to parent (Bubble) so it can update the iframe URL
		sendToParent({
			type: 'filtersChanged',
			filters: filterState,
		})
	}, [tabMode, filters, viewMode, isInitialized])

	// Apply client-side filtering
	const filteredSegments = useMemo(() => {
		let result = [...allSegments]

		// Tab-based filtering (client-side only for saved/recent)
		switch (tabMode) {
			case 'in-progress':
				// API handles is_active filter; keep a client-side guard to never show void shipments
				result = result.filter((s) => (s.order_status || '').trim().toUpperCase() !== 'V')
				break
			case 'saved':
				// Show only saved trips
				result = result.filter((s) => savedTripIds.has(s.trip_id))
				break
			case 'recent': {
				// Show only recent trips in order
				const recentMap = new Map(result.map((s) => [s.trip_id, s]))
				result = recentTripIds
					.map((id) => recentMap.get(id))
					.filter((s): s is TripSegment => s !== undefined)
				break
			}
			case 'all':
			default:
				// No additional filtering for 'all'
				break
		}

		// Keyword search (client-side)
		if (filters.keyword) {
			const kw = filters.keyword.toLowerCase()
			const beforeFilter = result.length
			result = result.filter(
				(s) =>
					s.pro_num.toLowerCase().includes(kw) ||
					s.meta?.bol_number?.toLowerCase().includes(kw) ||
					s.meta?.shipper?.toLowerCase().includes(kw) ||
					s.meta?.consignee?.toLowerCase().includes(kw) ||
					s.meta?.trailer?.toLowerCase().includes(kw)
			)

			// Show server search prompt if:
			// 1. Keyword is entered
			// 2. No results found in loaded data
			// 3. We didn't load all possible records (allSegments.length >= API limit)
			// 4. Not in saved/recent tabs (which are always client-side only)
			const shouldShowPrompt =
				result.length === 0 && beforeFilter >= 250 && tabMode !== 'saved' && tabMode !== 'recent'

			setShowServerSearchPrompt(shouldShowPrompt)
		} else {
			setShowServerSearchPrompt(false)
		}

		// Status filter only applies on the All tab, where users can refine by shipment status.
		if (tabMode === 'all' && filters.status) {
			const selectedStatus = normalizeStatusFilterValue(filters.status).toLowerCase()
			result = result.filter((s) => getSegmentStatusLabel(s).toLowerCase() === selectedStatus)
		}

		// Customer filter
		if (filters.customerId) {
			result = result.filter((s) => s.customer_id === filters.customerId)
		}

		// Shipper filter
		if (filters.shipperId) {
			result = result.filter((s) => s.meta?.shipper === filters.shipperId)
		}

		// Consignee filter
		if (filters.consigneeId) {
			result = result.filter((s) => s.meta?.consignee === filters.consigneeId)
		}

		// Sorting (skip for 'recent' tab as it should maintain recency order)
		if (tabMode !== 'recent') {
			result.sort((a, b) => {
				let cmp = 0
				switch (filters.sortBy) {
					case 'pro_num':
						cmp = a.pro_num.localeCompare(b.pro_num)
						break
					case 'status':
						cmp = getSegmentStatusLabel(a).localeCompare(getSegmentStatusLabel(b))
						break
					case 'shipper':
						cmp = (a.meta?.shipper || '').localeCompare(b.meta?.shipper || '')
						break
					case 'consignee':
						cmp = (a.meta?.consignee || '').localeCompare(b.meta?.consignee || '')
						break
					case 'last_updated':
						cmp = (a.last_ts || '').localeCompare(b.last_ts || '')
						break
				}
				return filters.sortDir === 'asc' ? cmp : -cmp
			})
		}

		return result
	}, [allSegments, filters, tabMode, savedTripIds, recentTripIds])

	// Handler for server-side search
	const handleServerSearch = () => {
		if (!filters.keyword || !token || !rawToken) return

		setIsLoadingSegments(true)
		setShowServerSearchPrompt(false)
		logBrowserDebug(
			debugConfig,
			'Running server-side keyword search',
			{
				keyword: filters.keyword,
				urlFilters,
			},
			'info'
		)

		// Fetch with keyword parameter sent to API
		fetchSegments(
			rawToken,
			API_URL,
			{
				...urlFilters,
				keyword: filters.keyword,
				limit: 1000, // Increase limit for searches
			},
			{
				enabled: debugConfig.enabled,
				traceId: debugConfig.traceId,
				requestSource: 'server-search',
			}
		)
			.then((segments) => {
				setAllSegments(segments)
				logBrowserDebug(
					debugConfig,
					segments.length === 0
						? 'Server-side keyword search returned no shipments'
						: 'Server-side keyword search updated segments',
					{
						keyword: filters.keyword,
						segments: summarizeSegments(segments),
					},
					segments.length === 0 ? 'warn' : 'log'
				)

				const proNumbers = [...new Set(segments.map((s) => s.pro_num))]
				sendToParent({ type: 'proNumbers', pro_numbers: proNumbers })
			})
			.catch((err) => {
				logBrowserDebug(
					debugConfig,
					'Server-side keyword search failed',
					{
						message: err instanceof Error ? err.message : String(err),
					},
					'error'
				)
			})
			.finally(() => {
				setIsLoadingSegments(false)
			})
	}

	// Paginated segments
	const paginatedSegments = useMemo(() => {
		const start = (filters.page - 1) * filters.resultsPerPage
		const end = start + filters.resultsPerPage
		return filteredSegments.slice(start, end)
	}, [filteredSegments, filters.page, filters.resultsPerPage])

	// Segments to display on map - respects user's mapMode preference
	const mapSegments = useMemo(() => {
		// Respect user's map mode preference
		if (filters.mapMode === 'show-all') {
			return filteredSegments
		}

		// Default: only show segments from current page (performance optimization)
		// If a trip is selected but not on current page, include it too (just that one)
		let segments = paginatedSegments
		if (selectedTripId) {
			const selectedSegment = filteredSegments.find(
				(s) => s.trip_id === selectedTripId || s.pro_num === selectedTripId
			)
			// If selected trip exists but isn't in current page, add it
			if (
				selectedSegment &&
				!paginatedSegments.find((s) => s.trip_id === selectedSegment.trip_id)
			) {
				segments = [...paginatedSegments, selectedSegment]
			}
		}

		return segments
	}, [filteredSegments, paginatedSegments, selectedTripId, filters.mapMode])

	// Selected segment
	const selectedSegment = useMemo(() => {
		if (!selectedTripId) return null
		return (
			allSegments.find((s) => s.trip_id === selectedTripId || s.pro_num === selectedTripId) || null
		)
	}, [allSegments, selectedTripId])

	// Parse URL params and restore filter state
	useEffect(() => {
		const params = new URLSearchParams(window.location.search)
		const tokenParam = params.get('token') ?? params.get('cfg')
		const customIcon = params.get('customIcon')
		const storedDebugConfig = readStoredDebugConfig()
		const nextDebugConfig: DebugConfig =
			isDebugValueEnabled(params.get('debug')) || storedDebugConfig.enabled
				? {
						enabled: true,
						traceId:
							params.get('debug_trace')?.trim() ||
							storedDebugConfig.traceId ||
							createDebugTraceId(),
					}
				: { enabled: false, traceId: '' }

		setDebugConfig(nextDebugConfig)
		persistDebugConfig(nextDebugConfig)
		logBrowserDebug(
			nextDebugConfig,
			'Bootstrapping shipments tracker',
			{
				apiURL: API_URL,
				hasToken: Boolean(tokenParam),
				searchParams: {
					date_range: params.get('date_range'),
					keyword: params.get('keyword'),
					order_status: params.get('order_status'),
					shipper_ids: params.get('shipper_ids'),
					consignee_ids: params.get('consignee_ids'),
					is_active: params.get('is_active'),
					limit: params.get('limit'),
				},
			},
			'info'
		)

		if (tokenParam) {
			try {
				const parsedToken = parseToken(tokenParam)
				setRawToken(tokenParam)
				setToken(parsedToken)
				logBrowserDebug(nextDebugConfig, 'Decoded token scope', summarizeToken(parsedToken), 'info')
			} catch (err) {
				logBrowserDebug(
					nextDebugConfig,
					'Failed to decode token',
					{
						message: err instanceof Error ? err.message : String(err),
					},
					'error'
				)
			}
		}

		if (customIcon === 'true') {
			setUseCustomIcon(true)
		}

		// Restore filter state from localStorage (fallback)
		let savedFilterState: any = {}
		try {
			const savedData = localStorage.getItem(STORAGE_KEYS.FILTER_STATE)
			if (savedData) {
				savedFilterState = JSON.parse(savedData)
			}
		} catch {
			// Ignore localStorage errors
		}

		// URL params take precedence over localStorage
		const tabModeParam = params.get('tabMode') as TabMode | null
		const viewModeParam = params.get('view') as ViewMode | null
		const dateRangeParam = params.get('dateRange')
		const anchorDateParam = params.get('anchorDate')
		const customStartDateParam = params.get('customStartDate')
		const customEndDateParam = params.get('customEndDate')
		const keywordParam = params.get('keyword')
		const statusParam = params.get('status')
		const customerIdParam = params.get('customerId')
		const shipperIdParam = params.get('shipperId')
		const consigneeIdParam = params.get('consigneeId')
		const sortByParam = params.get('sortBy')
		const sortDirParam = params.get('sortDir')
		const resultsPerPageParam = params.get('resultsPerPage')
		const pageParam = params.get('page')
		const mapModeParam = params.get('mapMode') as 'current-page' | 'show-all' | null
		const parsedUrlDateRange = parseApiDateRange(params.get('date_range'))

		// Set tab mode (URL > localStorage > default)
		if (tabModeParam && ['all', 'in-progress', 'saved', 'recent'].includes(tabModeParam)) {
			setTabMode(tabModeParam)
		} else if (savedFilterState.tabMode) {
			setTabMode(savedFilterState.tabMode)
		}

		// Set view mode (URL > localStorage > default)
		if (viewModeParam && ['map', 'list', 'split'].includes(viewModeParam)) {
			setViewMode(viewModeParam)
		} else if (savedFilterState.viewMode) {
			setViewMode(savedFilterState.viewMode)
		}

		// Set filters (URL > localStorage > default)
		const resolvedFilters: FilterState = {
			...filters,
			dateRange: dateRangeParam || savedFilterState.dateRange || filters.dateRange,
			anchorDate:
				anchorDateParam ||
				savedFilterState.anchorDate ||
				parsedUrlDateRange?.endDate ||
				filters.anchorDate,
			customStartDate:
				customStartDateParam ||
				savedFilterState.customStartDate ||
				(dateRangeParam === 'custom' ? parsedUrlDateRange?.startDate : undefined),
			customEndDate:
				customEndDateParam ||
				savedFilterState.customEndDate ||
				(dateRangeParam === 'custom' ? parsedUrlDateRange?.endDate : undefined),
			keyword: keywordParam || savedFilterState.keyword || filters.keyword,
			status: normalizeStatusFilterValue(statusParam || savedFilterState.status || filters.status),
			customerId: customerIdParam || savedFilterState.customerId || filters.customerId,
			shipperId: shipperIdParam || savedFilterState.shipperId || filters.shipperId,
			consigneeId: consigneeIdParam || savedFilterState.consigneeId || filters.consigneeId,
			sortBy: sortByParam || savedFilterState.sortBy || filters.sortBy,
			sortDir: (sortDirParam as 'asc' | 'desc') || savedFilterState.sortDir || filters.sortDir,
			resultsPerPage: resultsPerPageParam
				? parseInt(resultsPerPageParam, 10)
				: savedFilterState.resultsPerPage || filters.resultsPerPage,
			page: pageParam ? parseInt(pageParam, 10) : savedFilterState.page || filters.page,
			mapMode:
				mapModeParam && ['current-page', 'show-all'].includes(mapModeParam)
					? mapModeParam
					: savedFilterState.mapMode || filters.mapMode,
		}

		setFilters(resolvedFilters)

		// Read API filter parameters from URL (for backend filtering)
		const urlFiltersObj: SegmentFilters = {}
		const dateRange = params.get('date_range')
		const keyword = params.get('keyword')
		const orderStatus = params.get('order_status')
		const shipperIds = params.get('shipper_ids')
		const consigneeIds = params.get('consignee_ids')
		const isActive = params.get('is_active')
		const limitParam = params.get('limit')

		if (dateRange) urlFiltersObj.date_range = dateRange
		if (keyword) urlFiltersObj.keyword = keyword
		if (orderStatus) urlFiltersObj.order_status = orderStatus
		if (shipperIds) urlFiltersObj.shipper_ids = shipperIds
		if (consigneeIds) urlFiltersObj.consignee_ids = consigneeIds
		if (isActive) urlFiltersObj.is_active = isActive
		// Reduced default limit from 1000 to 250 for better initial load performance
		// Admin users can increase via URL param if needed
		urlFiltersObj.limit = limitParam ? parseInt(limitParam, 10) : 250
		urlFiltersObj.date_range = dateRange || buildApiDateRange(resolvedFilters)

		// Set is_active=true for in-progress tab
		const finalTabMode = tabModeParam || savedFilterState.tabMode || 'in-progress'
		if (finalTabMode === 'in-progress' && !urlFiltersObj.is_active) {
			urlFiltersObj.is_active = 'true'
		}

		setUrlFilters(urlFiltersObj)
		logBrowserDebug(nextDebugConfig, 'Resolved initial filters', {
			tabMode: finalTabMode,
			viewMode: viewModeParam || savedFilterState.viewMode || 'split',
			urlFilters: urlFiltersObj,
		})

		// Mark initialization as complete
		setIsInitialized(true)
	}, [])

	// Fetch segments
	useEffect(() => {
		if (token && rawToken) {
			if (!urlFilters.date_range) {
				logBrowserDebug(
					debugConfig,
					'Skipping shipments fetch until date_range is resolved',
					{
						urlFilters,
					},
					'info'
				)
				return
			}

			setIsLoadingSegments(true)
			logBrowserDebug(
				debugConfig,
				'Fetching shipments from API',
				{
					urlFilters,
					token: summarizeToken(token),
				},
				'info'
			)

			fetchSegments(rawToken, API_URL, urlFilters, {
				enabled: debugConfig.enabled,
				traceId: debugConfig.traceId,
				requestSource: 'initial-load',
			})
				.then((segments) => {
					setAllSegments(segments)
					logBrowserDebug(
						debugConfig,
						segments.length === 0 ? 'API returned no shipments' : 'Loaded shipments from API',
						{
							urlFilters,
							segments: summarizeSegments(segments),
						},
						segments.length === 0 ? 'warn' : 'log'
					)

					// Notify parent
					sendToParent({ type: 'ready' })

					const proNumbers = [...new Set(segments.map((s) => s.pro_num))]
					sendToParent({ type: 'proNumbers', pro_numbers: proNumbers })
				})
				.catch((err) => {
					logBrowserDebug(
						debugConfig,
						'Failed to fetch shipments from API',
						{
							message: err instanceof Error ? err.message : String(err),
							urlFilters,
						},
						'error'
					)
				})
				.finally(() => {
					setIsLoadingSegments(false)
				})
		}
	}, [token, rawToken, urlFilters, debugConfig])

	// Handle messages from parent
	usePostMessage((msg) => {
		if (msg.type === 'setDebug') {
			applyDebugConfig(msg.enabled, msg.traceId)
			return
		}

		logBrowserDebug(debugConfig, 'Received parent message', summarizeParentMessage(msg), 'info')

		if (msg.type === 'setTrips') {
			// Filter to specific trips
			if (msg.trip_ids.length > 0) {
				setFilters((f) => ({ ...f, keyword: msg.trip_ids[0] }))
			}
		}
		if (msg.type === 'setDateRange') {
			const dateWindow = normalizeDateWindow(msg.startDate, msg.endDate)

			setFilters((prev) => ({
				...prev,
				dateRange: 'custom',
				customStartDate: dateWindow.startDate,
				customEndDate: dateWindow.endDate,
				page: 1,
			}))
		}
		if (msg.type === 'fitAll') {
			setFitAllKey((n) => n + 1)
		}
		if (msg.type === 'highlight') {
			if (msg.trip_id === null) {
				setSelectedTripId(null)
			} else {
				setSelectedTripId(msg.trip_id)
			}
		}
		if (msg.type === 'updateSegment') {
			setAllSegments((prev) => {
				const idx = prev.findIndex((s) => s.trip_id === msg.segment.trip_id)
				if (idx >= 0) {
					const updated = [...prev]
					updated[idx] = msg.segment
					return updated
				}
				return [...prev, msg.segment]
			})
		}
	})

	useEffect(() => {
		if (!isInitialized || !debugConfig.enabled) return

		logBrowserDebug(debugConfig, 'Visibility snapshot', {
			tabMode,
			selectedTripId,
			loadedSegments: allSegments.length,
			filteredSegments: filteredSegments.length,
			paginatedSegments: paginatedSegments.length,
			mapSegments: mapSegments.length,
			clientFilters: {
				anchorDate: filters.anchorDate,
				customStartDate: filters.customStartDate,
				customEndDate: filters.customEndDate,
				dateRange: filters.dateRange,
				keyword: filters.keyword,
				status: filters.status,
				customerId: filters.customerId,
				shipperId: filters.shipperId,
				consigneeId: filters.consigneeId,
				page: filters.page,
				resultsPerPage: filters.resultsPerPage,
				mapMode: filters.mapMode,
			},
			urlFilters,
		})

		if (allSegments.length > 0 && filteredSegments.length === 0) {
			logBrowserDebug(
				debugConfig,
				'Client-side filters are hiding all loaded shipments',
				{
					loadedSegments: allSegments.length,
					tabMode,
					clientFilters: {
						anchorDate: filters.anchorDate,
						customStartDate: filters.customStartDate,
						customEndDate: filters.customEndDate,
						dateRange: filters.dateRange,
						keyword: filters.keyword,
						status: filters.status,
						customerId: filters.customerId,
						shipperId: filters.shipperId,
						consigneeId: filters.consigneeId,
						mapMode: filters.mapMode,
					},
				},
				'warn'
			)
		}
	}, [
		allSegments.length,
		debugConfig,
		filteredSegments.length,
		filters.consigneeId,
		filters.customEndDate,
		filters.customStartDate,
		filters.customerId,
		filters.dateRange,
		filters.keyword,
		filters.anchorDate,
		filters.mapMode,
		filters.page,
		filters.resultsPerPage,
		filters.shipperId,
		filters.status,
		isInitialized,
		mapSegments.length,
		paginatedSegments.length,
		selectedTripId,
		tabMode,
		urlFilters,
	])

	useEffect(() => {
		if (typeof window === 'undefined') return
		;(
			window as Window & {
				__shipmentsTrackerDebug?: {
					disable: () => void
					enable: (traceId?: string) => void
					enabled: boolean
					traceId: string
				}
			}
		).__shipmentsTrackerDebug = {
			enabled: debugConfig.enabled,
			traceId: debugConfig.traceId,
			enable: (traceId?: string) => applyDebugConfig(true, traceId),
			disable: () => applyDebugConfig(false),
		}
	}, [debugConfig, applyDebugConfig])

	const handleSelectTrip = (tripId: string | null) => {
		setSelectedTripId(tripId)

		if (!tripId) return

		// Add to recent trips (most recent first)
		setRecentTripIds((prev) => {
			// Remove if already exists, then add to front
			const filtered = prev.filter((id) => id !== tripId)
			const updated = [tripId, ...filtered].slice(0, MAX_RECENT_ITEMS)

			// Save to local storage
			try {
				localStorage.setItem(STORAGE_KEYS.RECENT_TRIPS, JSON.stringify(updated))
			} catch {
				// Ignore localStorage errors
			}

			return updated
		})

		// Notify parent
		const segment = allSegments.find((s) => s.trip_id === tripId)
		if (segment) {
			sendToParent({
				type: 'tripClicked',
				trip_id: segment.trip_id,
				pro_num: segment.pro_num,
				trailer: segment.meta?.trailer,
			})
		}
	}

	const handleToggleSave = (tripId: string) => {
		setSavedTripIds((prev) => {
			const next = new Set(prev)
			if (next.has(tripId)) {
				next.delete(tripId)
			} else {
				next.add(tripId)
			}

			// Save to local storage
			try {
				const savedArray = Array.from(next)
				localStorage.setItem(STORAGE_KEYS.SAVED_TRIPS, JSON.stringify(savedArray))
			} catch {
				// Ignore localStorage errors
			}

			return next
		})
	}

	const handleClosePanel = () => {
		setSelectedTripId(null)
		// Don't trigger fitAll - let user keep current zoom level
		// setFitAllKey((n) => n + 1)
	}

	if (!token) {
		return (
			<div style={styles.loading}>
				<div style={styles.spinner} />
				<span>Loading...</span>
			</div>
		)
	}

	return (
		<div style={styles.container}>
			{/* Tab mode selection */}
			<div style={styles.tabModeContainer}>
				<button
					onClick={(e) => {
						setTabMode('all')
						e.currentTarget.blur()
					}}
					style={{
						...styles.tabModeButton,
						...(tabMode === 'all' ? styles.tabModeButtonActive : {}),
					}}
				>
					All
				</button>
				<button
					onClick={(e) => {
						setTabMode('in-progress')
						e.currentTarget.blur()
					}}
					style={{
						...styles.tabModeButton,
						...(tabMode === 'in-progress' ? styles.tabModeButtonActive : {}),
					}}
				>
					In Progress
				</button>
				<button
					onClick={(e) => {
						setTabMode('saved')
						e.currentTarget.blur()
					}}
					style={{
						...styles.tabModeButton,
						...(tabMode === 'saved' ? styles.tabModeButtonActive : {}),
					}}
				>
					Saved
				</button>
				<button
					onClick={(e) => {
						setTabMode('recent')
						e.currentTarget.blur()
					}}
					style={{
						...styles.tabModeButton,
						...(tabMode === 'recent' ? styles.tabModeButtonActive : {}),
					}}
				>
					Recent
				</button>
			</div>

			{/* Filter Bar */}
			<FilterBar
				segments={allSegments}
				filters={filters}
				onFiltersChange={setFilters}
				totalResults={filteredSegments.length}
				displayedResults={paginatedSegments.length}
				showDateFilter={tabMode !== 'saved' && tabMode !== 'recent'}
				showStatusFilter={tabMode === 'all'}
				showServerSearchPrompt={showServerSearchPrompt}
				onServerSearch={handleServerSearch}
			/>

			{/* View mode tabs - directly above content */}
			<div style={styles.viewTabsContainer}>
				<button
					onClick={(e) => {
						setViewMode('split')
						e.currentTarget.blur()
					}}
					style={{
						...styles.viewTab,
						...(viewMode === 'split' ? styles.viewTabActive : {}),
					}}
				>
					Split View
				</button>
				<button
					onClick={(e) => {
						setViewMode('map')
						e.currentTarget.blur()
					}}
					style={{
						...styles.viewTab,
						...(viewMode === 'map' ? styles.viewTabActive : {}),
					}}
				>
					Map Only
				</button>
				<button
					onClick={(e) => {
						setViewMode('list')
						e.currentTarget.blur()
					}}
					style={{
						...styles.viewTab,
						...(viewMode === 'list' ? styles.viewTabActive : {}),
					}}
				>
					List Only
				</button>
			</div>

			{/* Main content area */}
			<div style={styles.content}>
				{/* Loading overlay */}
				{isLoadingSegments && (
					<div style={styles.loadingOverlay}>
						<div style={styles.loadingCard}>
							<div style={styles.spinner} />
							<span style={styles.loadingText}>Loading shipments...</span>
						</div>
					</div>
				)}

				{/* Map view */}
				{(viewMode === 'map' || viewMode === 'split') && (
					<div
						style={{
							...styles.mapContainer,
							flex: viewMode === 'split' ? '1 1 50%' : '1 1 100%',
						}}
					>
						<TruckMap
							segments={mapSegments}
							mapboxToken={MAPBOX_TOKEN}
							styleURL={token.styleURL}
							useCustomIcon={useCustomIcon}
							token={rawToken}
							apiURL={API_URL}
							selectedTripId={selectedTripId}
							onTripSelect={handleSelectTrip}
							fitAllKey={fitAllKey}
							viewMode={viewMode}
						/>
						{/* Permanent map mode toggle tabs */}
						<div style={styles.mapModeToggleContainer}>
							<button
								onClick={() => setFilters({ ...filters, mapMode: 'current-page' })}
								onMouseEnter={(e) => {
									if (filters.mapMode !== 'current-page') {
										e.currentTarget.style.backgroundColor = '#f3f4f6'
									}
								}}
								onMouseLeave={(e) => {
									if (filters.mapMode !== 'current-page') {
										e.currentTarget.style.backgroundColor = 'transparent'
									}
								}}
								style={{
									...styles.mapModeToggleTab,
									...(filters.mapMode === 'current-page' ? styles.mapModeToggleTabActive : {}),
								}}
							>
								Current Page
							</button>
							<button
								onClick={() => setFilters({ ...filters, mapMode: 'show-all' })}
								onMouseEnter={(e) => {
									if (filters.mapMode !== 'show-all') {
										e.currentTarget.style.backgroundColor = '#f3f4f6'
									}
								}}
								onMouseLeave={(e) => {
									if (filters.mapMode !== 'show-all') {
										e.currentTarget.style.backgroundColor = 'transparent'
									}
								}}
								style={{
									...styles.mapModeToggleTab,
									...(filters.mapMode === 'show-all' ? styles.mapModeToggleTabActive : {}),
								}}
							>
								Show All
							</button>
						</div>

						{/* Dismissable info banner - shows when large dataset */}
						{!mapModeBannerDismissed && filteredSegments.length > 100 && (
							<div
								style={{
									...(filters.mapMode === 'current-page'
										? styles.mapInfoBanner
										: styles.mapWarningBanner),
								}}
							>
								<span style={styles.mapBannerText}>
									{filters.mapMode === 'current-page' ? (
										<>
											Showing {paginatedSegments.length} of {filteredSegments.length} shipments. Use
											pagination or toggle "Show All" to see more.
										</>
									) : (
										<>
											Rendering {filteredSegments.length} shipments may impact performance. Switch
											to "Current Page" for faster interaction.
										</>
									)}
								</span>
								<button
									onClick={() => {
										setMapModeBannerDismissed(true)
										try {
											localStorage.setItem('shipments-tracker-map-banner-dismissed', 'true')
										} catch {
											// Ignore localStorage errors
										}
									}}
									onMouseEnter={(e) => {
										e.currentTarget.style.opacity = '1'
									}}
									onMouseLeave={(e) => {
										e.currentTarget.style.opacity = '0.8'
									}}
									style={styles.mapBannerClose}
									title="Dismiss"
								>
									✕
								</button>
							</div>
						)}
					</div>
				)}

				{/* List view */}
				{(viewMode === 'list' || viewMode === 'split') && (
					<div
						style={{
							...styles.listContainer,
							flex: viewMode === 'split' ? '1 1 50%' : '1 1 100%',
						}}
					>
						<ListView
							segments={paginatedSegments}
							selectedTripId={selectedTripId}
							onSelectTrip={handleSelectTrip}
							savedTripIds={savedTripIds}
							onToggleSave={handleToggleSave}
						/>
					</div>
				)}

				{/* Detail panel - pushes content to the left */}
				{selectedSegment && (
					<ShipmentDetailPanel segment={selectedSegment} onClose={handleClosePanel} />
				)}
			</div>
		</div>
	)
}

const styles: Record<string, React.CSSProperties> = {
	container: {
		display: 'flex',
		flexDirection: 'column',
		height: '100vh',
		overflow: 'hidden',
		backgroundColor: '#f9fafb',
	},
	loading: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		height: '100vh',
		gap: '12px',
		color: '#6b7280',
	},
	spinner: {
		width: '24px',
		height: '24px',
		border: '3px solid #e5e7eb',
		borderTopColor: '#3b82f6',
		borderRadius: '50%',
		animation: 'spin 1s linear infinite',
	},
	tabModeContainer: {
		display: 'flex',
		gap: '4px',
		padding: '8px 16px 0 16px',
		backgroundColor: '#ffffff',
		borderBottom: '1px solid #d0d5dd',
	},
	tabModeButton: {
		padding: '8px 20px',
		border: 'none',
		backgroundColor: 'rgba(255, 255, 255, 0)',
		fontSize: '14px',
		fontWeight: 500,
		color: '#6b7280',
		cursor: 'pointer',
		borderRadius: '0',
		transition: 'color 0.15s, border-color 0.15s',
		borderBottom: '2px solid transparent',
		marginBottom: '-1px',
		outline: 'none',
		WebkitTapHighlightColor: 'transparent',
	},
	tabModeButtonActive: {
		color: '#1e40af',
		borderBottom: '2px solid #1e40af',
		fontWeight: 600,
		backgroundColor: 'rgba(255, 255, 255, 0)',
	},
	viewTabsContainer: {
		display: 'flex',
		gap: '8px',
		padding: '6px 16px',
		backgroundColor: '#ffffff',
		borderBottom: '1px solid #d0d5dd',
	},
	viewTab: {
		padding: '6px 0',
		border: 'none',
		backgroundColor: '#ffffff',
		fontSize: '14px',
		fontWeight: 500,
		color: '#6b7280',
		cursor: 'pointer',
		borderRadius: '8px',
		transition: 'background-color 0.15s, color 0.15s',
		width: '95px',
		textAlign: 'center',
		outline: 'none',
		WebkitTapHighlightColor: 'transparent',
	},
	viewTabActive: {
		backgroundColor: '#eff6ff',
		color: '#1e40af',
		fontWeight: 600,
	},
	content: {
		display: 'flex',
		flex: 1,
		overflow: 'hidden',
	},
	mapContainer: {
		minHeight: '300px',
		position: 'relative',
	},
	listContainer: {
		overflow: 'auto',
		backgroundColor: '#ffffff',
	},
	loadingOverlay: {
		position: 'absolute',
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		backgroundColor: 'rgba(249, 250, 251, 0.95)',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		zIndex: 1000,
	},
	loadingCard: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		gap: '16px',
		padding: '32px 48px',
		backgroundColor: '#ffffff',
		borderRadius: '12px',
		boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
	},
	loadingText: {
		fontSize: '16px',
		fontWeight: 500,
		color: '#374151',
	},
	mapModeToggleContainer: {
		position: 'absolute',
		top: '10px',
		left: '10px',
		display: 'flex',
		gap: '2px',
		padding: '3px',
		backgroundColor: 'rgba(255, 255, 255, 0.95)',
		borderRadius: '6px',
		boxShadow: '0 1px 4px rgba(0, 0, 0, 0.15)',
		zIndex: 100,
	},
	mapModeToggleTab: {
		padding: '6px 12px',
		border: 'none',
		backgroundColor: 'transparent',
		color: '#6b7280',
		fontSize: '12px',
		fontWeight: 500,
		cursor: 'pointer',
		borderRadius: '4px',
		transition: 'all 0.15s',
		outline: 'none',
		whiteSpace: 'nowrap',
		userSelect: 'none',
	},
	mapModeToggleTabActive: {
		backgroundColor: '#3b82f6',
		color: '#ffffff',
		fontWeight: 600,
	},
	mapInfoBanner: {
		position: 'absolute',
		top: '50px',
		left: '10px',
		display: 'flex',
		alignItems: 'center',
		gap: '12px',
		padding: '10px 14px',
		backgroundColor: 'rgba(59, 130, 246, 0.95)',
		color: '#ffffff',
		borderRadius: '6px',
		fontSize: '12px',
		fontWeight: 500,
		boxShadow: '0 1px 4px rgba(0, 0, 0, 0.2)',
		zIndex: 99,
		maxWidth: '400px',
	},
	mapWarningBanner: {
		position: 'absolute',
		top: '50px',
		left: '10px',
		display: 'flex',
		alignItems: 'center',
		gap: '12px',
		padding: '10px 14px',
		backgroundColor: 'rgba(245, 158, 11, 0.95)',
		color: '#ffffff',
		borderRadius: '6px',
		fontSize: '12px',
		fontWeight: 500,
		boxShadow: '0 1px 4px rgba(0, 0, 0, 0.2)',
		zIndex: 99,
		maxWidth: '400px',
	},
	mapBannerText: {
		lineHeight: '1.4',
		flex: 1,
	},
	mapBannerClose: {
		background: 'none',
		border: 'none',
		color: '#ffffff',
		fontSize: '18px',
		cursor: 'pointer',
		padding: '0 4px',
		opacity: 0.8,
		transition: 'opacity 0.15s',
		outline: 'none',
		lineHeight: 1,
		flexShrink: 0,
	},
}
